import { Check, MapPin, MessageCircle, Phone, Video } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal, SectionHead } from "./Primitives";

export const About = () => {
  const { t } = useLang();
  return (
    <section id="about" data-testid="about-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 py-24 lg:py-36 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <SectionHead eyebrow={t.about.eyebrow} title={t.about.title} testid="about" />
        <Reveal delay={0.15} className="mt-12 flex items-end gap-4">
          <span className="display text-[8rem] sm:text-[10rem] leading-[0.8] text-[color:var(--saffron)]">20+</span>
          <span className="pb-4 text-sm uppercase tracking-[0.25em] text-[color:var(--muted)]">{t.about.years}</span>
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:pt-14 space-y-6 text-base md:text-lg leading-relaxed text-[color:var(--muted)]">
        <Reveal><p className="display text-2xl sm:text-3xl leading-snug text-[color:var(--ink)]">{t.about.p1}</p></Reveal>
        <Reveal delay={0.1}><p>{t.about.p2}</p></Reveal>
        <ul className="pt-4 grid sm:grid-cols-2 gap-4">
          {t.about.points.map((p, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <li data-testid={`about-point-${i}`} className="flex gap-3 text-sm text-[color:var(--ink)]/85">
                <Check className="w-4 h-4 mt-1 shrink-0 text-[color:var(--gold)]" />{p}
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};

const MODE_ICONS = [MapPin, Phone, Video, MessageCircle];

export const Process = () => {
  const { t } = useLang();
  return (
    <section data-testid="process-section" className="mx-auto max-w-[1440px] px-5 sm:px-8 pb-24 lg:pb-36">
      <SectionHead eyebrow={t.process.eyebrow} title={t.process.title} testid="process" align="right" />
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--line)] border border-[color:var(--line)] rounded-3xl overflow-hidden">
        {t.process.modes.map((m, i) => {
          const Icon = MODE_ICONS[i];
          return (
            <div key={i} data-testid={`mode-${i}`} className="bg-[color:var(--bg)] p-6 sm:p-8 hover:bg-[color:var(--surface)] transition-colors duration-500">
              <Icon className="w-6 h-6 text-[color:var(--saffron)]" strokeWidth={1.4} />
              <h3 className="mt-6 display text-2xl">{m.title}</h3>
              <p className="mt-2 text-sm text-[color:var(--muted)]">{m.text}</p>
            </div>
          );
        })}
      </div>
      <h3 className="mt-20 eyebrow">{t.process.stepsTitle}</h3>
      <ol className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {t.process.steps.map((s, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <li data-testid={`step-${i}`} className="relative pt-8 border-t border-[color:var(--gold)]/40">
              <span className="absolute -top-[9px] left-0 w-4 h-4 rounded-full bg-[color:var(--bg)] border border-[color:var(--gold)]" />
              <span className="display italic text-5xl text-[color:var(--gold)]">{i + 1}</span>
              <h4 className="mt-3 display text-2xl">{s.title}</h4>
              <p className="mt-2 text-sm text-[color:var(--muted)]">{s.text}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </section>
  );
};
